import { useAuth } from "@/auth/auth.ts";
import {
	type NetworkInspectionSnapshot,
	parseNetworkHar,
} from "@/lib/networkInspection.ts";
import type {
	SessionArtifact,
	SessionEvent,
	SessionLogLine,
} from "@limrun/api";
import useSWR from "swr";
import { orgRequestOptions } from "./org.ts";

export type { SessionArtifact, SessionEvent, SessionLogLine };

// SessionPlatform is the subset of platforms whose runtimes persist session
// artifacts (video recordings, app log and event captures).
export type SessionPlatform = "ios" | "android";

// useSessionArtifacts lists every persisted capture of the instance in one
// request; callers filter by kind. The endpoint carries the caller-declared
// platform (the two platforms return the same record shape), and the
// organization is pinned per request like the build-log hooks.
export function useSessionArtifacts(
	platform: SessionPlatform,
	instanceId: string | undefined,
	{
		organizationTid,
		enabled = true,
		refreshIntervalMs,
	}: {
		// Pinned per request, see orgRequestOptions.
		organizationTid?: string;
		enabled?: boolean;
		refreshIntervalMs?: number;
	} = {},
): {
	data: SessionArtifact[] | "loading";
	error?: Error;
} {
	const auth = useAuth();
	const { data, error } = useSWR<SessionArtifact[]>(
		enabled && instanceId
			? ["sessionArtifacts", platform, instanceId, organizationTid]
			: null,
		async () =>
			platform === "ios"
				? auth.regionClient.iosInstances.listSessionArtifacts(
						instanceId as string,
						undefined,
						orgRequestOptions(organizationTid),
					)
				: auth.regionClient.androidInstances.listSessionArtifacts(
						instanceId as string,
						undefined,
						orgRequestOptions(organizationTid),
					),
		{
			refreshInterval: refreshIntervalMs,
		},
	);
	return {
		data: data ?? "loading",
		error: error instanceof Error ? error : undefined,
	};
}

// parseJsonl parses a persisted JSONL capture into typed entries, skipping
// malformed lines (a capture cut off mid-line at SIGTERM still renders).
export function parseJsonl<T extends { ts: number }>(text: string): T[] {
	const entries: T[] = [];
	for (const line of text.split("\n")) {
		const trimmed = line.trim();
		if (!trimmed) continue;
		try {
			const parsed = JSON.parse(trimmed) as T;
			if (typeof parsed.ts === "number") entries.push(parsed);
		} catch {
			// Skip malformed line.
		}
	}
	return entries;
}

// fetchJsonlCapture downloads a persisted JSONL capture and parses it into
// typed entries. Called once per capture and memoized by the caller (SWR
// keyed by artifact id) since persisted content is immutable.
export async function fetchJsonlCapture<T extends { ts: number }>(
	downloadUrl: string,
): Promise<T[]> {
	const res = await fetch(downloadUrl);
	if (!res.ok) {
		throw new Error(`failed to download capture: HTTP ${res.status}`);
	}
	// Persistence order is not ts order: the runtime stamps a coalesced
	// event with its start time but appends it at flush, so a tap can land
	// after a longer gesture that started earlier. Playback reveal
	// binary-searches on ts, so sort here.
	return parseJsonl<T>(await res.text()).sort((a, b) => a.ts - b.ts);
}

// HAR is parsed only after the user expands a network artifact. Bound the
// download even when object storage omits Content-Length so a corrupt or
// unexpectedly large artifact cannot exhaust the console tab.
export const MAX_NETWORK_LOG_DOWNLOAD_BYTES = 64 * 1024 * 1024;

export async function fetchNetworkLogCapture(
	downloadUrl: string,
): Promise<NetworkInspectionSnapshot> {
	const response = await fetch(downloadUrl);
	if (!response.ok) {
		throw new Error(
			`failed to download network capture: HTTP ${response.status}`,
		);
	}
	const declaredSize = Number(response.headers.get("content-length"));
	if (
		Number.isFinite(declaredSize) &&
		declaredSize > MAX_NETWORK_LOG_DOWNLOAD_BYTES
	) {
		throw new Error("network capture is too large to inspect safely");
	}
	if (!response.body) {
		const data = await response.arrayBuffer();
		if (data.byteLength > MAX_NETWORK_LOG_DOWNLOAD_BYTES) {
			throw new Error("network capture is too large to inspect safely");
		}
		return parseNetworkHar(new TextDecoder().decode(data));
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let text = "";
	let bytes = 0;
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			bytes += value.byteLength;
			if (bytes > MAX_NETWORK_LOG_DOWNLOAD_BYTES) {
				await reader.cancel();
				throw new Error("network capture is too large to inspect safely");
			}
			text += decoder.decode(value, { stream: true });
		}
		text += decoder.decode();
	} finally {
		reader.releaseLock();
	}
	return parseNetworkHar(text);
}

export function useNetworkLogCapture(
	artifact: SessionArtifact | undefined,
	enabled: boolean,
): {
	data: NetworkInspectionSnapshot | "loading";
	error?: Error;
} {
	const { data, error } = useSWR<NetworkInspectionSnapshot>(
		enabled && artifact ? ["networkLogCapture", artifact.id] : null,
		() => fetchNetworkLogCapture((artifact as SessionArtifact).downloadUrl),
	);
	return {
		data: data ?? "loading",
		error: error instanceof Error ? error : undefined,
	};
}
